import React from 'react';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { ShieldCheck, FileText, AlertCircle, X } from 'lucide-react';

export type LegalModalType = 'privacy' | 'terms' | 'allergen' | null;

interface LegalModalProps {
  type: LegalModalType;
  onClose: () => void;
}

export const LegalModal: React.FC<LegalModalProps> = ({ type, onClose }) => {
  const { legalSettings, restaurantProfile } = useApp();
  const { lang } = useLanguage();

  if (!type) return null;

  let title = '';
  let icon = <ShieldCheck size={24} color="var(--accent-emerald)" />;
  let content = '';

  if (type === 'privacy') {
    title = lang === 'en' ? 'Privacy Policy (EU GDPR)' : 'Tietosuojaseloste (EU GDPR)';
    icon = <ShieldCheck size={24} color="var(--accent-emerald)" />;
    content = lang === 'en' ? legalSettings.privacy_policy_en : legalSettings.privacy_policy_fi;
  } else if (type === 'terms') {
    title = lang === 'en' ? 'Terms of Ordering & Service' : 'Tilaus- ja Käyttöehdot';
    icon = <FileText size={24} color="var(--accent-copper)" />;
    content = lang === 'en' ? legalSettings.terms_of_service_en : legalSettings.terms_of_service_fi;
  } else if (type === 'allergen') {
    title = lang === 'en' ? 'Allergens & Food Safety (Oiva)' : 'Allergeenit & Oiva-raportti';
    icon = <AlertCircle size={24} color="var(--accent-amber)" />;
    content = lang === 'en' ? legalSettings.allergen_policy_en : legalSettings.allergen_policy_fi;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-card"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '640px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {icon}
            <div className="modal-title" style={{ fontSize: '1.15rem' }}>{title}</div>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div
          className="modal-body"
          style={{
            overflowY: 'auto',
            whiteSpace: 'pre-line',
            lineHeight: '1.6',
            fontSize: '0.88rem',
            color: 'var(--text-secondary)'
          }}
        >
          {content}

          {/* Official Business Details Footer inside modal */}
          <div
            style={{
              marginTop: '20px',
              paddingTop: '16px',
              borderTop: '1px solid var(--border-subtle)',
              fontSize: '0.78rem',
              color: 'var(--text-muted)'
            }}
          >
            <div><strong>{restaurantProfile.name}</strong> • Y-tunnus: {restaurantProfile.business_id}</div>
            <div>{restaurantProfile.address}, {restaurantProfile.postal_code} {restaurantProfile.city}, Finland</div>
            <div>{restaurantProfile.domain} • {restaurantProfile.phone}</div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn-primary" onClick={onClose} style={{ width: '100%' }}>
            {lang === 'en' ? 'Close' : 'Sulje'}
          </button>
        </div>
      </div>
    </div>
  );
};
